import { ActionButton } from "@components:ActionButton";
import { Box, Button, Stack, Typography } from "@mui/material";
import { RecordingCard } from "@pages:app/audio/components/RecordingCard";
import { RecordingPlayer } from "@pages:app/audio/components/RecordingPlayer";
import { AudioPlayer, TranscriptionJob } from "@pages:app/audio/hooks";
import { requestMeta } from "@pages:app/audio/util";
import { useTranslation } from "react-i18next";
import { MdErrorOutline, MdRefresh } from "react-icons/md";

interface FailedCardProps {
  job: TranscriptionJob;
  player: AudioPlayer;
}

/*
 * The recording after its job failed: what went wrong, and the two ways out.
 */
export const FailedCard = ({ job, player }: FailedCardProps) => {
  const { t } = useTranslation();
  const detail = job.taskStatus?.error || job.error;

  return (
    <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
      <RecordingCard
        file={job.file}
        meta={requestMeta(
          {
            ...job,
            durationSeconds: job.waveform?.durationSeconds,
          },
          t,
        )}
        top={<RecordingPlayer player={player} waveform={job.waveform} />}
      >
        <Box sx={{ display: "flex", color: "error.main" }}>
          <MdErrorOutline size={22} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>
          {t("audio.failed.title")}
        </Typography>
      </RecordingCard>

      {detail && (
        <Typography
          role="alert"
          sx={{
            fontSize: 14,
            color: "text.secondary",
            overflowWrap: "anywhere",
          }}
        >
          {detail}
        </Typography>
      )}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <ActionButton onClick={job.transcribe} startIcon={<MdRefresh />}>
          {t("audio.failed.tryAgain")}
        </ActionButton>
        <Button variant="outlined" onClick={job.reset}>
          {t("audio.failed.chooseAnother")}
        </Button>
      </Stack>
    </Stack>
  );
};
